// Pure player state machine (PLAY03, PLAY04). No DOM, no timers: the controller feeds it events.
export type PlayRequest = {
  sourceId: string;
  mode: 'live' | 'vod' | 'catchup' | 'start_over';
  title: string;
  subtitle?: string;
  itemId?: string;        // movie/episode id for progress (VOD03)
  seriesId?: string;      // set for episodes: next-episode lookup when one ends (VOD02)
  channelId?: string;     // live channel id for recent/previous channel
  programId?: string;
  positionSec?: number;
  artworkId?: string | null;
};
/** external = the session is an ADR-0006 external link (HTTP-only provider on an HTTPS app): no in-app playback. */
export type PlayerStatus = 'idle' | 'resolving' | 'connecting' | 'playing' | 'paused' | 'buffering' | 'recovering' | 'ended' | 'failed' | 'external';
export type PlayerError = {
  code: string;
  message: string;
  retryable: boolean;
  requestId?: string;
  httpStatus?: number;
  details?: Record<string, unknown>;
};

export const MAX_RECONNECTS = 2;
export const MAX_MEDIA_RECOVERIES = 2;
/** PLAY04: never retried automatically (auth, exhausted connections, forbidden, undecodable, missing). */
export const NO_RETRY: ReadonlySet<string> = new Set([
  'CONNECTION_LIMIT', 'SESSION_EXPIRED', 'RESOURCE_FORBIDDEN', 'UNSUPPORTED_MEDIA', 'ARCHIVE_UNAVAILABLE',
  'PIN_REQUIRED', 'NOT_FOUND', 'UNAUTHORIZED', 'START_TIMEOUT', 'CONVERSION_FAILED',
]);

export type MachineState = { status: PlayerStatus; error: PlayerError | null; reconnects: number; mediaRecoveries: number };
export type MediaEvent = 'play' | 'playing' | 'pause' | 'waiting' | 'ended';
export type MachineEvent =
  | { type: 'play' }
  | { type: 'resolved' }
  | { type: 'external' }
  | { type: 'fail'; error: PlayerError }
  | { type: 'fault'; kind: 'network' | 'media'; error: PlayerError }
  | { type: 'media'; event: MediaEvent }
  | { type: 'stable' }
  | { type: 'stop' };

export const initialState: MachineState = { status: 'idle', error: null, reconnects: 0, mediaRecoveries: 0 };

const ACTIVE: ReadonlySet<PlayerStatus> = new Set(['connecting', 'playing', 'paused', 'buffering', 'recovering', 'ended']);
/** Media element event → [statuses it applies from, resulting status]. Anything else is ignored. */
const MEDIA: Record<MediaEvent, [PlayerStatus[], PlayerStatus]> = {
  play: [['paused'], 'buffering'],
  playing: [['connecting', 'paused', 'buffering', 'recovering', 'ended'], 'playing'],
  pause: [['connecting', 'playing', 'buffering'], 'paused'],
  waiting: [['playing'], 'buffering'],
  ended: [['playing', 'buffering', 'paused'], 'ended'],
};

export function reduce(s: MachineState, e: MachineEvent): MachineState {
  switch (e.type) {
    case 'play': return { ...initialState, status: 'resolving' };
    case 'stop': return s.status === 'idle' ? s : initialState;
    case 'resolved': return s.status === 'resolving' ? { ...s, status: 'connecting' } : s;
    case 'external': return s.status === 'connecting' || s.status === 'recovering' ? { ...s, status: 'external' } : s;
    case 'fail': return s.status === 'idle' ? s : { ...s, status: 'failed', error: e.error };
    case 'stable': return s.status === 'playing' && (s.reconnects || s.mediaRecoveries) ? { ...s, reconnects: 0, mediaRecoveries: 0 } : s;
    case 'fault': {
      if (!ACTIVE.has(s.status)) return s;
      if (NO_RETRY.has(e.error.code)) return { ...s, status: 'failed', error: e.error };
      if (e.kind === 'network' && s.reconnects < MAX_RECONNECTS) return { ...s, status: 'recovering', reconnects: s.reconnects + 1 };
      if (e.kind === 'media' && s.mediaRecoveries < MAX_MEDIA_RECOVERIES) return { ...s, status: 'recovering', mediaRecoveries: s.mediaRecoveries + 1 };
      return { ...s, status: 'failed', error: e.error };
    }
    case 'media': {
      const [from, to] = MEDIA[e.event];
      return from.includes(s.status) && s.status !== to ? { ...s, status: to } : s;
    }
  }
}
